/**
 * EventBus - Pub/Sub System for SDK Communication
 *
 * A simple, type-safe event bus that enables decoupled communication
 * between SDK components. All components communicate through events
 * rather than direct method calls.
 *
 * Usage:
 * ```typescript
 * const bus = new EventBus();
 *
 * // Subscribe to an event
 * const unsubscribe = bus.on('trigger:fired', (data) => {
 *   console.log('Trigger fired:', data.triggerType);
 * });
 *
 * // Emit an event
 * bus.emit('trigger:fired', {
 *   timestamp: Date.now(),
 *   triggerType: 'exit_intent',
 *   campaignId: 'camp_123',
 *   campaignName: 'Exit Popup',
 *   context: { isExitIntent: true }
 * });
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */

import type {
  EventBusConfig,
  EventHandler,
  SDKEvents,
  SDKEventName,
  Unsubscribe,
} from '../types/event-bus.types';

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<EventBusConfig> = {
  debug: false,
  maxListeners: 100,
};

export class EventBus {
  private listeners: Map<string, Set<EventHandler>>;
  private config: Required<EventBusConfig>;

  constructor(config?: EventBusConfig) {
    this.listeners = new Map();
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Subscribe to an event
   *
   * @param event - Event name to subscribe to
   * @param handler - Function to call when event is emitted
   * @returns Unsubscribe function to remove the listener
   */
  public on<E extends SDKEventName>(
    event: E,
    handler: EventHandler<SDKEvents[E]>
  ): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const handlers = this.listeners.get(event)!;

    // Check max listeners to prevent memory leaks
    if (handlers.size >= this.config.maxListeners) {
      console.warn(
        `[EventBus] Warning: Event "${event}" has ${handlers.size} listeners. ` +
          `This might indicate a memory leak.`
      );
    }

    handlers.add(handler as EventHandler);

    if (this.config.debug) {
      console.log(`[EventBus] Subscribed to "${event}" (${handlers.size} listeners)`);
    }

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event
   *
   * @param event - Event name to unsubscribe from
   * @param handler - Handler function to remove
   */
  public off<E extends SDKEventName>(
    event: E,
    handler: EventHandler<SDKEvents[E]>
  ): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;

    handlers.delete(handler as EventHandler);

    if (this.config.debug) {
      console.log(`[EventBus] Unsubscribed from "${event}" (${handlers.size} listeners)`);
    }

    // Clean up empty sets
    if (handlers.size === 0) {
      this.listeners.delete(event);
    }
  }

  /**
   * Subscribe to an event for one-time execution
   *
   * @param event - Event name to subscribe to
   * @param handler - Function to call once when event is emitted
   * @returns Unsubscribe function to remove the listener before it fires
   */
  public once<E extends SDKEventName>(
    event: E,
    handler: EventHandler<SDKEvents[E]>
  ): Unsubscribe {
    const wrappedHandler: EventHandler<SDKEvents[E]> = (data) => {
      this.off(event, wrappedHandler);
      handler(data);
    };

    return this.on(event, wrappedHandler);
  }

  /**
   * Emit an event to all subscribers
   *
   * @param event - Event name to emit
   * @param data - Event payload
   */
  public emit<E extends SDKEventName>(event: E, data: SDKEvents[E]): void {
    const handlers = this.listeners.get(event);

    if (this.config.debug) {
      console.log(`[EventBus] Emitting "${event}"`, data);
    }

    if (!handlers || handlers.size === 0) {
      if (this.config.debug) {
        console.log(`[EventBus] No listeners for "${event}"`);
      }
      return;
    }

    // Call all handlers
    for (const handler of handlers) {
      try {
        handler(data);
      } catch (error) {
        console.error(`[EventBus] Error in handler for "${event}":`, error);
      }
    }
  }

  /**
   * Check if an event has any listeners
   *
   * @param event - Event name to check
   * @returns True if event has listeners
   */
  public hasListeners<E extends SDKEventName>(event: E): boolean {
    const handlers = this.listeners.get(event);
    return handlers !== undefined && handlers.size > 0;
  }

  /**
   * Get the number of listeners for an event
   *
   * @param event - Event name to check
   * @returns Number of listeners
   */
  public listenerCount<E extends SDKEventName>(event: E): number {
    const handlers = this.listeners.get(event);
    return handlers?.size ?? 0;
  }

  /**
   * Remove all listeners for a specific event
   *
   * @param event - Event name to clear
   */
  public removeAllListeners<E extends SDKEventName>(event: E): void {
    this.listeners.delete(event);

    if (this.config.debug) {
      console.log(`[EventBus] Removed all listeners for "${event}"`);
    }
  }

  /**
   * Clear all listeners from all events
   * Use this for cleanup/destroy
   */
  public clear(): void {
    const eventCount = this.listeners.size;
    this.listeners.clear();

    if (this.config.debug) {
      console.log(`[EventBus] Cleared all listeners (${eventCount} events)`);
    }
  }

  /**
   * Get all registered event names
   *
   * @returns Array of event names with listeners
   */
  public getEventNames(): string[] {
    return Array.from(this.listeners.keys());
  }
}
